import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { apiErrorMessage } from '../lib/apiError';
import { FormFields, useFormValues, type Field, type FormValues } from './FormFields';

/** A small form in a dialog: checks its fields, saves, refreshes what's on screen, then closes. */
export function FormDialog({
  title,
  intro,
  fields,
  initial,
  submitLabel = 'Save',
  onSubmit,
  onClose,
}: {
  title: string;
  intro?: string;
  fields: Field[];
  initial: FormValues;
  submitLabel?: string;
  onSubmit: (values: FormValues) => Promise<unknown>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const form = useFormValues(fields, initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.validate()) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form.values);
      await queryClient.invalidateQueries();
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={(event) => void submit(event)}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {intro && <Typography color="text.secondary">{intro}</Typography>}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
          <FormFields
            fields={fields}
            values={form.values}
            errors={form.errors}
            onChange={form.onChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving…' : submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
